import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import DriverService from "app/data/drivers/driversService";
import { DriverPayInfo, hasConfiguredPayRates } from "../buildTripSettlementData";
import { extractDriverPayInfo } from "../extractDriverPayInfo";

const fetchDriverPayRates = async (driverId: string): Promise<DriverPayInfo | undefined> => {
  try {
    const response = await DriverService.getInstance().getDriver(driverId);
    const payInfo = extractDriverPayInfo(response?.data);
    if (hasConfiguredPayRates(payInfo)) {
      return payInfo;
    }
  } catch {
    // Fall through to list search when direct lookup fails or has no rates.
  }

  try {
    const response = await DriverService.getInstance().getDrivers("", driverId, 0);
    const match = response.data?.items?.find(
      (driver) => String(driver.driverId) === String(driverId)
    );
    const payInfo = extractDriverPayInfo(match);
    return hasConfiguredPayRates(payInfo) ? payInfo : undefined;
  } catch {
    return undefined;
  }
};

export const useDriverPayRates = (driverId: string) => {
  const { data: payInfo, isLoading, isFetched } = useQuery({
    queryKey: [QUERY_KEYS.DRIVER_DETAILS, driverId, "pay-rates"],
    queryFn: () => fetchDriverPayRates(driverId),
    enabled: !!driverId,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    payInfo,
    isLoadingPayRates: isLoading,
    payRatesUnavailable: !!driverId && isFetched && !hasConfiguredPayRates(payInfo),
  };
};
